/**
 * The button primitive, shadcn/ui shape: a `variant` x `size` class table plus
 * `cn()` so a call site's own className wins.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: it is a plain `<button>`, not a Radix Slot,
 * and it adds no `disabled` logic of its own. `disabled` on this surface is
 * RENDERING -- it tells an operator something is happening -- and it is
 * explicitly not the re-entrancy gate; that lives in `lib/gate.ts` and must stay
 * there (see its docblock on why React state cannot refuse the second click of
 * a double-click).
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'icon';

const VARIANT: Record<ButtonVariant, string> = {
  default:
    'bg-accent text-accent-fg border-transparent hover:bg-accent/90 hover:border-transparent shadow-card',
  secondary:
    'bg-surface-2 text-text border-line hover:bg-surface',
  outline:
    'bg-transparent text-text border-line hover:bg-surface-2',
  ghost:
    'bg-transparent text-muted border-transparent hover:bg-surface-2 hover:text-text hover:border-transparent',
  destructive:
    'bg-transparent text-danger border-danger/35 hover:bg-danger-soft hover:border-danger',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1.5 px-2.5 text-xs',
  md: 'h-8 gap-2 px-3 text-[0.8125rem]',
  icon: 'size-8 p-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-field border font-medium',
        'transition-colors disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:size-3.5 [&_svg]:shrink-0',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...props}
    />
  );
});
